<template>
    <AdminLayout>
        <div class="p-6">
            <h1 class="text-2xl font-bold mb-4">Veraltete Messungen</h1>

            <!-- Filters -->
            <div class="flex gap-4 mb-4 items-end">
                <div class="flex-grow">
                    <label class="block font-bold mb-1 text-xs uppercase text-gray-500">Suche</label>
                    <div class="relative w-full">
                        <InputText
                            v-model="searchQuery"
                            placeholder="Suche nach Name oder E-Mail..."
                            @input="debouncedSearch"
                            class="w-full !h-[42px]"
                        />
                        <Button
                            v-if="searchQuery"
                            @click="clearSearch"
                            icon="pi pi-times"
                            class="p-button-text p-button-sm absolute right-2 top-1/2 -translate-y-1/2"
                            aria-label="Clear search"
                        />
                    </div>
                </div>

                <div v-if="isSuperAdmin" class="w-64 shrink-0">
                    <label class="block font-bold mb-1 text-xs uppercase text-gray-500">Unternehmen</label>
                    <Dropdown
                        v-model="selectedCompany"
                        :options="companyOptions"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Alle Unternehmen"
                        @change="handleFilterChange"
                        class="w-full !h-[42px] flex items-center"
                        showClear
                        filter
                    />
                </div>
            </div>

            <!-- Data Table -->
            <DataTable
                v-if="customers.data.length > 0"
                :value="customers.data"
                responsiveLayout="scroll"
                :paginator="false"
                class="p-datatable-sm"
            >
                <Column field="customer_name" header="Kundenname">
                    <template #body="slotProps">
                        <a
                            :href="`/customers/${slotProps.data.uuid}/plan`"
                            class="text-blue-600 hover:underline cursor-pointer"
                        >
                            {{ slotProps.data.first_name }} {{ slotProps.data.last_name }}
                        </a>
                    </template>
                </Column>
                <Column field="email" header="Kunden-E-Mail" />
                <Column field="measurement_updated_at" header="Letzte Messung">
                    <template #body="slotProps">
                        {{ formatDate(slotProps.data.measurement_updated_at) }}
                    </template>
                </Column>
                <Column field="days_since_measurement" header="Tage seit Messung">
                    <template #body="slotProps">
                        {{ slotProps.data.days_since_measurement }} Tage
                    </template>
                </Column>
                <Column field="notification_status" header="Status">
                    <template #body="slotProps">
                        <Tag
                            :value="formatNotificationStatus(slotProps.data.notification_status)"
                            :severity="getNotificationSeverity(slotProps.data.notification_status)"
                        />
                    </template>
                </Column>
                <Column field="notification_sent_at" header="Gesendet am">
                    <template #body="slotProps">
                        {{ formatDateTime(slotProps.data.notification_sent_at) }}
                    </template>
                </Column>
            </DataTable>

            <!-- Empty State -->
            <div v-if="customers.data.length === 0" class="text-center py-8 text-gray-500">
                Keine Kunden mit veralteten Messungen gefunden.
            </div>

            <!-- Pagination -->
            <div class="mt-6 flex justify-center items-center gap-2">
                <Button
                    icon="pi pi-chevron-left"
                    :disabled="!customers.prev_page_url"
                    @click="goToPage(customers.prev_page_url)"
                    text
                    rounded
                />
                <template v-for="(page, index) in paginationPages" :key="index">
                    <span v-if="page === '...'" class="px-3 py-2 text-gray-500">...</span>
                    <Button
                        v-else
                        :label="String(page)"
                        @click="goToPageNumber(page)"
                        :severity="customers.current_page === page ? 'primary' : 'secondary'"
                        :text="customers.current_page !== page"
                        rounded
                    />
                </template>
                <Button
                    icon="pi pi-chevron-right"
                    :disabled="!customers.next_page_url"
                    @click="goToPage(customers.next_page_url)"
                    text
                    rounded
                />
            </div>
        </div>
    </AdminLayout>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Inertia } from '@inertiajs/inertia';
import AdminLayout from '../../../Layouts/AdminLayout.vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Dropdown from 'primevue/dropdown';
import { debounce } from 'lodash';

const props = defineProps({
    customers: Object,
    filters: Object,
    isSuperAdmin: {
        type: Boolean,
        default: false
    },
    companyOptions: {
        type: Array,
        default: () => []
    },
});

const searchQuery = ref(props.filters.search || '');
const selectedCompany = ref(props.filters.company_id);

// Generate pagination with ellipsis
const paginationPages = computed(() => {
    const current = props.customers.current_page;
    const last = props.customers.last_page;
    const pages = [];

    if (last <= 7) {
        // Show all pages if 7 or fewer
        for (let i = 1; i <= last; i++) {
            pages.push(i);
        }
    } else {
        // Always show first page
        pages.push(1);

        if (current <= 3) {
            // Near the beginning
            pages.push(2, 3, 4, '...', last);
        } else if (current >= last - 2) {
            // Near the end
            pages.push('...', last - 3, last - 2, last - 1, last);
        } else {
            // In the middle
            pages.push('...', current - 1, current, current + 1, '...', last);
        }
    }

    return pages;
});

const handleFilterChange = () => {
    const params = {
        search: searchQuery.value,
        page: 1
    };

    if (props.isSuperAdmin) {
        params.company_id = selectedCompany.value;
    }

    Inertia.get('/admin/stale-measurements', params, {
        preserveState: true,
        preserveScroll: true
    });
};

const debouncedSearch = debounce(() => {
    handleFilterChange();
}, 300);

const clearSearch = () => {
    searchQuery.value = '';
    handleFilterChange();
};

const goToPage = (url) => {
    if (url) {
        Inertia.get(url, {}, {
            preserveState: true,
            preserveScroll: true,
        });
    }
};

const goToPageNumber = (page) => {
    const params = {
        page: page,
        search: searchQuery.value,
    };

    if (props.isSuperAdmin) {
        params.company_id = selectedCompany.value;
    }

    Inertia.get('/admin/stale-measurements', params, {
        preserveState: true,
        preserveScroll: true,
    });
};

const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
};

const formatDateTime = (dateTime) => {
    if (!dateTime) return '-';
    return new Date(dateTime).toLocaleString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const formatNotificationStatus = (status) => {
    const statusMap = {
        'sent': 'Gesendet',
        'error': 'Fehler',
        'no_tokens': 'Keine Tokens',
        'not_sent': 'Nicht gesendet'
    };
    return statusMap[status] || status;
};

const getNotificationSeverity = (status) => {
    const severityMap = {
        'sent': 'success',
        'error': 'danger',
        'no_tokens': 'warning',
        'not_sent': 'secondary'
    };
    return severityMap[status] || 'secondary';
};
</script>
